import { useMemo } from "react";

import clsx from "clsx";
import { isEmpty, orderBy } from "lodash";

import { useUser } from "@components/user-context";
import { type DataTypeProvider } from "@devexpress/dx-react-grid";
import { MiniGraph } from "@features/creative-analytics/components/ca-card";
import { usePageConfiguration } from "@features/sales-v3/hooks";
import { useAvailableMetrics } from "@features/sales-v3/hooks/use-available-metrics";
import {
  useActiveTabState,
  useTabState,
} from "@features/sales-v3/hooks/use-tab-state";
import { type ColumnDef } from "@features/sales-v3/hooks/use-table-columns";
import { type SalesTableVisualRow } from "@features/sales-v3/types/table";
import {
  type BenchmarkIndicatorRow,
  UniversalBenchmarksIndicator,
  useUniversalBenchmarks,
} from "@features/universal-benchmarks";
import Spinner from "@shared/spinner";

import { MetricsSection } from "./metrics-section";
import { TikTokShopsColumnTooltip } from "./tik-tok-shops-column-tooltip";

export const MetricDataFormatter = ({
  column,
  row: _row,
  isSummaryRow,
}: DataTypeProvider.ValueFormatterProps & { isSummaryRow?: boolean }) => {
  const row = _row as SalesTableVisualRow;
  const columnDef = column as ColumnDef;
  const isBenchmark = columnDef.metric?.metadata?.isBenchmark;
  const isPositiveChangeGood = columnDef.metric?.isPositiveChangeGood;
  const fractionChange = row?.changeFractions?.[columnDef.id];
  const benchmark = row?.benchmarks?.[columnDef.id];

  const canShowMiniGraph =
    columnDef.showMiniGraph && !isBenchmark && !columnDef.loading;

  const { user } = useUser();

  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    filters,
    breakdowns,
  } = usePageConfiguration();
  const { tabState: breakdownTabState } = useTabState("breakdown");
  const { isBreakdownTab } = useActiveTabState();

  const { benchmarkBreakdowns, benchmarkFilters } = useMemo(() => {
    let benchmarkFilters = [];

    for (const filter of filters) {
      for (const value of filter.values) {
        benchmarkFilters.push({ breakdown: filter.key, value });
      }
    }

    if (!isBreakdownTab && isSummaryRow) {
      for (const row of breakdownTabState.proposedFiltersState
        .inclusionAtomicities) {
        try {
          for (const atomicity of JSON.parse(row.atomicity)) {
            for (const [breakdown, value] of Object.entries(atomicity)) {
              benchmarkFilters.push({ breakdown, value: value as string });
            }
          }
          // eslint-disable-next-line no-empty
        } catch {}
      }
    }

    if (!row) {
      return { benchmarkBreakdowns: null, benchmarkFilters };
    }

    if (row.key === "default") {
      return { benchmarkBreakdowns: null, benchmarkFilters };
    }

    let benchmarkBreakdowns: Record<string, string>[] = [];

    if (breakdowns && breakdowns.length > 0) {
      for (const breakdown of breakdowns) {
        benchmarkBreakdowns.push({
          [breakdown.key]: row.rowMetadata.dimensions[breakdown.key],
        });
      }
    } else {
      benchmarkBreakdowns.push({
        "Platform (Northbeam)":
          row.rowMetadata.dimensions["Platform (Northbeam)"],
      });
    }

    let parsedRowKey;

    try {
      parsedRowKey = JSON.parse(row.key);
      // eslint-disable-next-line no-empty
    } catch {}

    if (Array.isArray(parsedRowKey)) {
      benchmarkBreakdowns = parsedRowKey;
    } else {
      benchmarkFilters = [];
    }

    return { benchmarkBreakdowns, benchmarkFilters };
  }, [
    row?.key,
    row?.rowMetadata,
    breakdowns,
    filters,
    breakdownTabState,
    isSummaryRow,
  ]);

  const universalBenchmark = useUniversalBenchmarks({
    level:
      (row?.rowMetadata.tab === "breakdown"
        ? "campaign"
        : row?.rowMetadata.tab) ?? "campaign",
    attributionModel,
    attributionWindow,
    accountingMode,
    breakdowns: benchmarkBreakdowns,
    filters: benchmarkFilters,
    metric: columnDef.metric
      ? {
          id: columnDef.metric.metadata.baseId,
          isLtv: columnDef.metric.metadata.isLtv,
        }
      : undefined,
  });

  const benchmarkKpi = useMemo(() => {
    if (!row) {
      return null;
    }

    if (!universalBenchmark) {
      return null;
    }

    if (universalBenchmark.value === null) {
      return null;
    }

    if (!columnDef.metric) {
      return null;
    }

    const spendValue = row.export?.["spend"];

    if (!spendValue) {
      return null;
    }

    const metric = columnDef.metric;

    return {
      name:
        metric.name ??
        metric.metadata.name ??
        metric.spec?.name ??
        metric.metadata.salesTableMetricId,
      current: row.export?.[columnDef.id],
      target: universalBenchmark.value,
      format: metric.format,
      positiveTrend: metric.isPositiveChangeGood ? "up" : "down",
    } as BenchmarkIndicatorRow;
  }, [
    row,
    columnDef.metric,
    columnDef.id,
    user.currencies.currentCurrency,
    universalBenchmark,
  ]);

  return (
    <TikTokShopsColumnTooltip row={row} columnDef={columnDef}>
      <div className="flex items-center justify-between h-full px-2 w-full">
        {canShowMiniGraph && (
          <div className="h-8 w-16 mr-2 ">
            <MetricMiniGraph row={row} columnDef={columnDef} />
          </div>
        )}
        {(row?.isHeaderRow || benchmark) && columnDef.loading && (
          <div className="animate-pulse flex-grow w-full">
            <div className="h-2 w-2/5 bg-gray-2"></div>
          </div>
        )}
        {isBenchmark && !columnDef.loading && (
          <div
            className={clsx("font-mono font-light rounded-xl px-2 py-1", {
              "text-alien+2 bg-alien-2":
                benchmark?.percentDisplay !== "0.0%" &&
                (isPositiveChangeGood
                  ? (benchmark?.value ?? 0) > 0
                  : (benchmark?.value ?? 0) < 0),
              "text-mars+2 bg-mars-2":
                benchmark?.percentDisplay !== "0.0%" &&
                (isPositiveChangeGood
                  ? (benchmark?.value ?? 0) < 0
                  : (benchmark?.value ?? 0) > 0),
              "text-interstellar-1 bg-gray-1":
                benchmark?.percentDisplay === "0.0%",
            })}
          >
            {benchmark?.percentDisplay &&
              benchmark.percentDisplay !== "0.0%" && (
                <i
                  className={clsx("fa-regular fa-sharp text-[0.7rem] mr-1.5", {
                    "fa-arrow-up": (benchmark?.value ?? 0) >= 0,
                    "fa-arrow-down": (benchmark?.value ?? 0) < 0,
                  })}
                ></i>
              )}
            {benchmark?.percentDisplay ?? "—%"}
          </div>
        )}
        {universalBenchmark?.status === "loading" && (
          <Spinner colorClassname="w-3 h-3 text-cosmic text-xxs" />
        )}
        {benchmarkKpi && (
          <UniversalBenchmarksIndicator
            benchmarkItemName={row?.rowMetadata?.naming?.name}
            benchmarkIndicatorRows={[benchmarkKpi]}
          />
        )}
        <MetricsSection
          className="flex-grow"
          value={
            isBenchmark
              ? benchmark?.valueDisplay || "-"
              : row?.display?.[columnDef.id]
          }
          fractionChangeValue={fractionChange ?? 0}
          showChangeFraction={!isEmpty(row?.changeFractions) && !isBenchmark}
          isPositiveChangeGood={columnDef.metric.isPositiveChangeGood ?? true}
          compareDataLoading={columnDef.compareDataLoading}
        />
      </div>
    </TikTokShopsColumnTooltip>
  );
};

function MetricMiniGraph({
  row,
  columnDef,
}: {
  row: SalesTableVisualRow;
  columnDef: ColumnDef;
}) {
  const { getMetricById } = useAvailableMetrics();

  const nbChartData = useMemo(() => {
    const dailyMetrics = orderBy(row?.dailyMetrics, "date", "asc");

    const metricSpec = columnDef.metric;

    if (!dailyMetrics?.length || !metricSpec) {
      return {
        data: new Array<number>(dailyMetrics?.length ?? 10).fill(0),
        average: 0,
      };
    }

    let total = 0;
    let validCounts = 0;

    const adData = dailyMetrics.map((metricDay) => {
      const modifiedDay = (
        metricSpec?.requiredNBCalculatedMetrics || []
      ).reduce((prev, requiredMetric) => {
        const reqMetricSpec = getMetricById(requiredMetric);

        return {
          ...prev,
          [requiredMetric]: reqMetricSpec
            ? reqMetricSpec.calculate(metricDay)
            : 0,
        };
      }, metricDay);
      const value = metricSpec ? metricSpec.calculate(modifiedDay) : 0;
      if (!isNaN(value) && isFinite(value)) {
        total += value;
        validCounts += 1;
      }
      return value;
    });

    return { data: adData, average: validCounts ? total / validCounts : 0 };
  }, [row?.dailyMetrics, columnDef.metric, getMetricById]);

  return (
    <MiniGraph
      data={nbChartData.data}
      average={nbChartData.average}
      isPositiveChangeGood={columnDef.metric.isPositiveChangeGood}
    />
  );
}
